import { api, apiPath, request } from './client';
import { routeForApiModule } from '@/data/modules';
import type { OwnerAssignmentPreview, ConfirmOwnerAssignment } from '@/types/owner-assignment';
import type {
  HealthStatus,
  Milestone,
  ProjectDeclaration,
  AdminUser,
  AppNotification,
  AdminUserCleanupCandidate,
  ClosedProject,
  ClosureReadiness,
  ClosureReport,
  AdminUserOptions,
  ModuleCatalogItem,
  ModuleRecord,
  ModuleRecordsResponse,
  Deliverable,
  DeliverableStatus,
  DeliverableWritePayload,
  MyDeliverables,
  AuthSession,
  PlanImportPreview,
  PartDrawing,
  PartDrawingDetail,
  PortfolioResponse,
  Project,
  ProjectDetail,
  ProjectInitializationPreview,
  ProjectInitializationRequest,
  ProjectInitializationResult,
  PlanRelayPreview,
  PlanRelayResult,
  FmeaTreeResponse,
  PfmeaBatchPreview,
  PfmeaImportPreview,
  DimensionalReportDetail,
  DimensionalSheetModel,
  DimensionalReportSummaryItem,
  SpecialCharacteristicsResponse,
  ProjectPlanResponse,
  ProjectStatus,
  ProjectWritePayload,
  WorkItem,
  WorkItemAction,
  WorkItemSource,
  WorkResponse,
  TeamVacancies,
} from '@/types/domain';
import type {
  DelayStatsResponse, NodeDelayStatsResponse, NodeSummaryResponse, NodeSummaryScope, SnapshotTrends, TaskTimelineResponse, TaskTypeSuggestion,
  TrackerResponse, TrackerScope,
} from '@/types/tasks';
import type {
  BomLedgerDetail, BomLedgerRow, BomLedgerSummary, BomSyncResult,
} from '@/types/bom-ledger';

interface RawWorkbenchResponse {
  identity?: { id: number; displayName: string; source: string };
  counts?: Record<string, number>;
  total?: number;
  upcoming?: number;
  returned?: number;
  truncated?: boolean;
  items: Array<Record<string, unknown>>;
}

interface RawProjectSchedule {
  projectId: number | string;
  nodes: Array<Record<string, unknown>>;
}

function normalizedWorkItem(item: Record<string, unknown>): WorkItem {
  const dueAt = typeof item.dueAt === 'string' ? item.dueAt : null;
  const dueTime = dueAt ? new Date(dueAt).getTime() : Number.NaN;
  const soonThreshold = Date.now() + 7 * 86_400_000;
  const overdue = Boolean(item.overdue);
  const kind = item.type === 'approval' ? 'approval'
    : item.type === 'review' ? 'review'
      : item.type === 'message' ? 'message' : 'task';
  const moduleKey = String(item.module || '');
  const projectId = item.projectId as string | number | null | undefined;
  return {
    id: item.id as string | number,
    kind,
    title: String(item.title || ''),
    projectId,
    projectNo: typeof item.projectNo === 'string' ? item.projectNo : null,
    projectName: typeof item.projectName === 'string' ? item.projectName : null,
    moduleKey,
    status: String(item.status || 'pending'),
    dueDate: dueAt,
    timing: overdue ? 'overdue' : Number.isFinite(dueTime) && dueTime <= soonThreshold ? 'due-soon' : 'normal',
    assignee: null,
    recordPath: projectId ? `/projects/${projectId}/overview` : routeForApiModule(moduleKey),
    source: (item.source === 'plan-node' || item.source === 'deliverable'
      ? item.source
      : 'wf_work_items') as WorkItemSource,
    referenceId: ((item.reference as Record<string, unknown> | undefined)?.id
      ?? null) as string | number | null,
    canUpdateStatus: item.canUpdateStatus !== false,
    actions: Array.isArray(item.actions) ? (item.actions as WorkItemAction[]) : [],
    startDate: typeof item.startAt === 'string' ? item.startAt : null,
    // 只有后端明说「没触发」才算；旧接口没有这个字段时一律当眼下的待办
    notStarted: item.triggered === false,
  };
}

function normalizedSchedule(payload: RawProjectSchedule): ProjectPlanResponse {
  return {
    asOf: new Date().toISOString(),
    projectId: payload.projectId,
    tasks: payload.nodes.map((node) => {
      const reviewRoles = Array.isArray(node.requiredReviewRoles) ? node.requiredReviewRoles : [];
      return {
        id: node.id as string | number,
        activityId: (node.activityId as string | number | null) ?? null,
        parentId: (node.parentActivityId as string | number | null) ?? null,
        phaseCode: String(node.typeRaw || node.nodeType || '节点'),
        name: String(node.name || ''),
        nodeType: String(node.nodeType || 'task'),
        sortOrder: typeof node.sortOrder === 'number' ? node.sortOrder : null,
        applicable: node.applicable !== false,
        applicabilityReason: typeof node.applicabilityReason === 'string' ? node.applicabilityReason : null,
        /** PDM 里手工建的节点才允许删除；旧系统同步来的只能设为不适用 */
        isManual: node.isManual === true,
        ownerRole: typeof node.responsibility === 'string' ? node.responsibility : null,
        ownerUserId: typeof node.ownerUserId === 'number' ? node.ownerUserId : null,
        ownerName: typeof node.ownerName === 'string' ? node.ownerName : null,
        ownerSource: typeof node.ownerSource === 'string' ? node.ownerSource : null,
        isMine: node.isMine === true,
        isRelated: node.isRelated === true,
        relationKinds: Array.isArray(node.relationKinds)
          ? node.relationKinds.filter((kind): kind is 'owner' | 'reviewer' | 'deliverable-owner' =>
            kind === 'owner' || kind === 'reviewer' || kind === 'deliverable-owner')
          : [],
        planStart: typeof node.plannedStartAt === 'string' ? node.plannedStartAt : null,
        planEnd: typeof node.plannedEndAt === 'string' ? node.plannedEndAt : null,
        actualEnd: typeof node.actualAt === 'string' ? node.actualAt : null,
        status: String(node.status || 'pending'),
        progress: typeof node.progress === 'number' ? node.progress : null,
        keyDeliverable: Boolean(node.isKeyDeliverable),
        approvalRequired: reviewRoles.length > 0 || (node.approvalWorkflowId !== null && node.approvalWorkflowId !== undefined),
        // 交付物在审批流里的实际状态：有在审的就是「待评审」（0820 第 6 条）
        approvalStatus: Number(node.reviewPending ?? 0) > 0
          ? 'pending'
          : Number(node.reviewApproved ?? 0) > 0 ? 'approved' : null,
        overdue: Boolean(node.isOverdue),
        outputs: Array.isArray(node.outputs) ? node.outputs.map(String) : [],
        reviewRoles: reviewRoles.map(String),
        versionDescription: typeof node.versionDescription === 'string' ? node.versionDescription : null,
      };
    }),
  };
}

function normalizedProjectDetail(payload: Record<string, unknown>): ProjectDetail {
  const milestones = Array.isArray(payload.milestones) ? payload.milestones as Array<Record<string, unknown>> : [];
  return {
    ...(payload as unknown as Project),
    plan: payload.plan as ProjectDetail['plan'],
    milestones: milestones.map((item) => ({
      id: item.id as string | number,
      milestoneCode: String(item.milestone_code || item.milestoneCode || ''),
      planDate: typeof (item.plan_date || item.planDate) === 'string' ? String(item.plan_date || item.planDate) : null,
      actualDate: typeof (item.actual_date || item.actualDate) === 'string' ? String(item.actual_date || item.actualDate) : null,
      gateStatus: typeof (item.gate_status || item.gateStatus) === 'string' ? String(item.gate_status || item.gateStatus) : null,
      remark: typeof item.remark === 'string' ? item.remark : null,
      planDateSource: typeof (item.plan_date_source || item.planDateSource) === 'string'
        ? String(item.plan_date_source || item.planDateSource) : null,
      actualDateSource: typeof (item.actual_date_source || item.actualDateSource) === 'string'
        ? String(item.actual_date_source || item.actualDateSource) : null,
      // 挽回计划（0923）：项目灯靠它分黄红
      recoveryDate: typeof (item.recovery_date || item.recoveryDate) === 'string'
        ? String(item.recovery_date || item.recoveryDate) : null,
      recoveryNote: typeof (item.recovery_note || item.recoveryNote) === 'string'
        ? String(item.recovery_note || item.recoveryNote) : null,
      recoveryByName: typeof (item.recovery_by_name || item.recoveryByName) === 'string'
        ? String(item.recovery_by_name || item.recoveryByName) : null,
      recoveryAt: typeof (item.recovery_at || item.recoveryAt) === 'string'
        ? String(item.recovery_at || item.recoveryAt) : null,
      recoveryCount: Number(item.recovery_count ?? item.recoveryCount ?? 0),
    })),
    members: Array.isArray(payload.members) ? payload.members as ModuleRecord[] : [],
  };
}

/**
 * 分页取全一份列表。
 *
 * 后端单次上限 200、默认只给 50。不翻页就会静默少数据——
 * 这类缺失没有任何报错，用的人只会以为「系统里没有」，
 * 比接口直接失败难发现得多（2026-08-12 秦岭OP3 文件页 346 个只显示 50 个）。
 * 2000 条封顶是防跑飞；真到那个量级该做界面分页，而不是继续闷头拉。
 */
async function pagedRecords(path: string): Promise<{ items: ModuleRecord[] }> {
  const pageSize = 200;
  const joiner = path.includes('?') ? '&' : '?';
  const items: ModuleRecord[] = [];
  for (let offset = 0; offset < 2000; offset += pageSize) {
    const page = await api.get<{ items: ModuleRecord[] }>(
      `${path}${joiner}limit=${pageSize}&offset=${offset}`,
    );
    const batch = page.items ?? [];
    items.push(...batch);
    if (batch.length < pageSize) break;
  }
  return { items };
}
/**
 * 一条问题「我能改什么」。后端 ModuleToolsService.issueEditRights 的返回形状。
 *
 * 两档不是一个开关：内容（质量/PM/提出者/责任人）和
 * 状态 + 实际完成时间（质量/PM/提出者，**责任人不在这一档**）。
 */
export interface IssueEscalationTargets {
  issueId: number;
  projectId: number | null;
  currentLevel: 1 | 2 | 3 | 4;
  floorLevel: 1 | 2 | 3 | 4;
  /** 地板从哪来（A 级 / 客户升级投诉）；没地板为 null */
  floorReason?: string | null;
  canRaise: boolean;
  canLower: boolean;
  levels: Array<{ level: 1 | 2 | 3 | 4; label: string; people: Array<{ id: number; name: string }>; missing: string | null }>;
  history: Array<{
    fromLevel: number; toLevel: number; source: string; reason: string;
    actorName: string | null; targetNames: string | null; createdAt: string;
  }>;
}

export interface EscalationBoardItem {
  id: number; code: string | null; title: string; status: string; ownerName: string | null;
  severityGrade: string | null; customerComplaint: string | null; planDate: string | null;
  /** 只认「当前计划日期」这一格（planDate 没有时会退到要求完成日期） */
  currentPlanDate?: string | null;
  level: 1 | 2 | 3 | 4; levelLabel: string; escalatedAt: string | null; daysAtLevel: number | null;
  remindersSinceLevel: number; projectId: number | null; projectNo: string | null; projectName: string | null;
  lastReason: string | null; lastActor: string | null; targetNames: string | null; advice: string | null;
  /** 0923 起看板上就地办：详情 + 这一行我能做什么（后端算好） */
  floorLevel?: 1 | 2 | 3 | 4;
  description?: string | null; rootCause?: string | null; countermeasure?: string | null;
  category?: string | null; foundBy?: string | null;
  /** 最近几条进展，新的在前 */
  progress?: string[];
  history?: Array<{
    fromLevel: number; toLevel: number; source: string; reason: string;
    actorName: string | null; targetNames: string | null; createdAt: string;
  }>;
  can?: { editContent: boolean; close: boolean; raise: boolean; lower: boolean; remind: boolean };
  /** 和我有关（0929）：我管的项目、我是责任人、升到我这个主管、升到管理层而我是总监 */
  mine?: boolean;
  /** 按项目分组时抬头用（0929）：项目经理姓名（多人「、」连），逾期天数（没到期 0，没填计划 null） */
  projectManager?: string | null;
  overdueDays?: number | null;
}

export interface IssueEscalationBoard {
  generatedAt: string;
  levels: Array<{ level: number; key: string; label: string; tone: string }>;
  counts: Record<string, number>;
  /** 和我有关的那些，同一口径分层数（0929） */
  countsMine?: Record<string, number>;
  /** 总监、管理员默认看全部；其他人默认只看和自己有关的 */
  viewer?: { defaultScope: 'mine' | 'all' };
  escalated: EscalationBoardItem[];
  suggested: EscalationBoardItem[];
}

export interface IssueEditRights {
  issueId: number;
  projectId: number | null;
  canEditContent: boolean;
  canEditStatus: boolean;
  as: Array<'quality' | 'project_manager' | 'reporter' | 'owner'>;
  foundBy: string | null;
  ownerName: string | null;
}

/** NAS 主存状态（documents/storage-status）：文件管理页状态条的数据源 */
export interface StorageStatus {
  nasReachable: boolean;
  capacity: { totalBytes: number; freeBytes: number } | null;
  /** 落在本地暂存区、等 NAS 恢复后自动搬回的文件数 */
  spillFiles: number;
  /** 待整理到 NAS 树路径的版本数（-1 = 后端查询失败） */
  pendingOrganize: number;
}

export const eqsApi = {
  portfolio: () => api.get<PortfolioResponse>('portfolio'),
  formOptions: () => api.get<Record<string, Array<{ value: string; label: string }>>>('form-options'),
  mailInbound: () => api.get<Record<string, unknown>>('mail-inbound'),
  /** 人力资源架构树：部门 → 人，供团队选人用 */
  orgTree: () => api.get<Record<string, unknown>>('org-tree'),
  /** 审批流：流程 + 步骤 + 可选角色 */
  approvalWorkflowAdmin: () => api.get<Record<string, unknown>>('approval-workflows'),
  approvalWorkflowCandidates: (search?: string) =>
    api.get<Record<string, unknown>>(`approval-workflows/candidates${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  createApprovalWorkflow: (payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>('approval-workflows', payload),
  updateApprovalWorkflow: (id: number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`approval-workflows/${id}`, payload),
  deleteApprovalWorkflow: (id: number, moveTo?: number | 'clear') =>
    /*
      moveTo = 挂着这条流程的节点怎么办（0824）：
      不传＝先问清楚（后端会把受影响清单抛回来）、'clear'＝清空、数字＝改挂过去。
    */
    api.delete<Record<string, unknown>>(
      `approval-workflows/${id}${moveTo === undefined ? '' : `?moveTo=${moveTo}`}`),
  /** 把流程挂到一批节点；workflowId 传 null 表示清空 */
  attachApprovalWorkflow: (workflowId: number | null, nodeIds: number[]) =>
    api.post<Record<string, unknown>>('approval-workflows/attach', { workflowId, nodeIds }),
  /** 本人改自己的密码；管理员重置别人的走 admin/users 那条 */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<Record<string, unknown>>('auth/change-password', { currentPassword, newPassword }),
  session: () => api.get<AuthSession>('session/me'),
  login: (username: string, password: string) =>
    api.post<AuthSession>('auth/login', { username, password }),
  logout: () => request<void>('auth/logout', { method: 'POST' }),
  adminUsers: (query = '') =>
    api.get<{ items: AdminUser[]; total: number; page: number; pageSize: number }>(
      `admin/users${query ? `?${query}` : ''}`,
    ),
  adminUserOptions: () => api.get<AdminUserOptions>('admin/users/options'),
  /* 结项（移交量产）：体检 → 发起 → 总监拍板（0828） */
  projectClosureReadiness: (projectId: string | number) =>
    api.get<ClosureReadiness>(`projects/${projectId}/closure/readiness`),
  projectClosureReport: (projectId: string | number) =>
    api.get<ClosureReport>(`projects/${projectId}/closure/report`),
  submitProjectClosure: (
    projectId: string | number,
    payload: { note?: string; waivers?: Array<{ key: string; note: string }> },
  ) => api.post<{ closureId: number; status: string }>(`projects/${projectId}/closure/submit`, payload),
  decideProjectClosure: (
    projectId: string | number, closureId: number,
    payload: { approve: boolean; note?: string },
  ) => api.post<{ closureId: number; status: string }>(
    `projects/${projectId}/closure/${closureId}/decide`, payload),
  closedThisYear: () =>
    api.get<{ items: ClosedProject[]; total: number }>('closures/this-year'),
  /* 清理用户名单：部门误建 / 废弃账号 / 还挂着东西不能动（0828） */
  adminUserCleanupCandidates: () =>
    api.get<{
      org: AdminUserCleanupCandidate[];
      stale: AdminUserCleanupCandidate[];
      blocked: AdminUserCleanupCandidate[];
    }>('admin/users/cleanup'),
  runAdminUserCleanup: (ids: number[]) =>
    api.post<{
      removed: AdminUserCleanupCandidate[];
      archived: AdminUserCleanupCandidate[];
      refused: AdminUserCleanupCandidate[];
    }>('admin/users/cleanup', { ids }),
  createAdminUser: (payload: Record<string, unknown>) =>
    api.post<AdminUser>('admin/users', payload),
  updateAdminUser: (userId: number, payload: Record<string, unknown>) =>
    api.patch<AdminUser>(`admin/users/${userId}`, payload),
  updateAdminUserAccess: (
    userId: number,
    payload: { roles: string[]; projectIds: number[] },
  ) => api.patch<AdminUser>(`admin/users/${userId}/access`, payload),
  setAdminUserTemporaryPassword: (userId: number, temporaryPassword: string) =>
    api.post<{ updated: true }>(
      `admin/users/${userId}/temporary-password`,
      { temporaryPassword },
    ),
  /**
   * 开通登录：服务端随机生成临时密码、邮件发给本人、首次登录强制改密（0827 Tony）。
   * 发出去了就不回密码；只有那个人没有可用邮箱时才回给管理员转交。
   */
  openAdminUserLogin: (userId: number) =>
    api.post<{
      updated: true; delivered: 'email' | 'returned'; email?: string; password?: string;
    }>(`admin/users/${userId}/temporary-password`, { generate: true, enableLogin: true }),
  // ---- 会议(2026-08-25) ----
  meetingSeries: () => api.get<Record<string, unknown>>('meetings/series'),
  createMeetingSeries: (payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>('meetings/series', payload),
  updateMeetingSeries: (id: number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`meetings/series/${id}`, payload),
  meetings: (view: 'upcoming' | 'past', projectId?: number) =>
    api.get<Record<string, unknown>>(
      `meetings?view=${view}${projectId ? `&projectId=${projectId}` : ''}`),
  meetingDetail: (id: number) => api.get<Record<string, unknown>>(`meetings/${id}`),
  createMeeting: (payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>('meetings', payload),
  regenerateAgenda: (id: number) =>
    api.post<Record<string, unknown>>(`meetings/${id}/agenda/generate`, {}),
  addAgendaItem: (id: number, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`meetings/${id}/agenda`, payload),
  patchAgendaItem: (id: number, itemId: number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`meetings/${id}/agenda/${itemId}`, payload),
  patchMeetingAttendees: (id: number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`meetings/${id}/attendees`, payload),
  confirmMeetingAgenda: (id: number) =>
    api.post<Record<string, unknown>>(`meetings/${id}/confirm-agenda`, {}),
  unconfirmMeetingAgenda: (id: number) =>
    api.post<Record<string, unknown>>(`meetings/${id}/unconfirm-agenda`, {}),
  updateMeeting: (id: number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`meetings/${id}`, payload),
  startMeeting: (id: number) => api.post<Record<string, unknown>>(`meetings/${id}/start`, {}),
  recordMeetingOutcome: (id: number, itemId: number, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`meetings/${id}/agenda/${itemId}/outcomes`, payload),
  deleteMeetingOutcome: (id: number, outcomeId: number) =>
    api.delete<Record<string, unknown>>(`meetings/${id}/outcomes/${outcomeId}`),
  closeMeeting: (id: number, presentUserIds?: number[]) =>
    api.post<Record<string, unknown>>(`meetings/${id}/close`, presentUserIds ? { presentUserIds } : {}),
  /** AI 解析:自由文本 → 结论草稿(不落库,人确认后逐条 recordMeetingOutcome) */
  extractMeetingOutcomes: (id: number, text: string) =>
    api.post<Record<string, unknown>>(`meetings/${id}/extract-outcomes`, { text }),
  reopenMeeting: (id: number) => api.post<Record<string, unknown>>(`meetings/${id}/reopen`, {}),
  cancelMeeting: (id: number) => api.post<Record<string, unknown>>(`meetings/${id}/cancel`, {}),
  // ---- 派活(2026-08-25):和会中「转待办」是同一能力的两个入口 ----
  createDelegation: (payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>('tasks/delegations', payload),
  parseDelegation: (text: string) =>
    api.post<Record<string, unknown>>('tasks/delegations/parse', { text }),
  sentDelegations: () => api.get<Record<string, unknown>>('tasks/delegations?view=sent'),
  cancelDelegation: (id: number) =>
    api.post<Record<string, unknown>>(`tasks/delegations/${id}/cancel`, {}),
  // ---- 任务跟踪表(2026-09-03):全员看全部;项目页页签走同一端点加 projectId ----
  taskTracker: (query: { scope?: TrackerScope; projectId?: number | null; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (query.scope) params.set('scope', query.scope);
    if (query.projectId) params.set('projectId', String(query.projectId));
    if (query.limit) params.set('limit', String(query.limit));
    const suffix = params.toString();
    return api.get<TrackerResponse>(`tasks/tracker${suffix ? `?${suffix}` : ''}`);
  },
  taskTypes: () => api.get<{ types: TaskTypeSuggestion[] }>('tasks/types'),
  requestTaskHold: (workItemId: number, reason: string) =>
    api.post<Record<string, unknown>>(`tasks/items/${workItemId}/hold`, { reason }),
  decideTaskHold: (requestId: number, approve: boolean, note?: string) =>
    api.post<Record<string, unknown>>(`tasks/holds/${requestId}/decide`, note ? { approve, note } : { approve }),
  remindTask: (workItemId: number) =>
    api.post<{ reminded: boolean; skipped: string | null; reminders: number }>(`tasks/items/${workItemId}/remind`, {}),
  editDelegation: (delegationId: number, payload: Record<string, unknown>) =>
    api.patch<{ id: number; changed: string[]; summary?: string }>(`tasks/delegations/${delegationId}`, payload),
  addTaskNote: (workItemId: number, body: string) =>
    api.post<{ id: number; createdAt: string; actorName: string; body: string }>(`tasks/items/${workItemId}/notes`, { body }),
  taskTimeline: (workItemId: number) => api.get<TaskTimelineResponse>(`tasks/items/${workItemId}/timeline`),
  rescheduleTask: (workItemId: number, payload: { dueAt: string; category: string; reason: string }) =>
    api.post<{ rescheduleId: number; dueAt: string; delayDays: number; role: string }>(`tasks/items/${workItemId}/reschedule`, payload),
  delayStats: (query: { days?: number; projectId?: number | null; scope?: 'all' | 'team' } = {}) => {
    const params = new URLSearchParams();
    if (query.days) params.set('days', String(query.days));
    if (query.projectId) params.set('projectId', String(query.projectId));
    if (query.scope) params.set('scope', query.scope);
    const suffix = params.toString();
    return api.get<DelayStatsResponse>(`tasks/delays${suffix ? `?${suffix}` : ''}`);
  },
  /** 阶段计划的延期统计（0926）：谁在改计划、为什么改；权限和派活那份同一档 */
  nodeDelayStats: (query: { days?: number; projectId?: number | null; scope?: 'all' | 'team' } = {}) => {
    const params = new URLSearchParams();
    if (query.days) params.set('days', String(query.days));
    if (query.projectId) params.set('projectId', String(query.projectId));
    if (query.scope) params.set('scope', query.scope);
    const suffix = params.toString();
    return api.get<NodeDelayStatsResponse>(`projects/nodes/delays${suffix ? `?${suffix}` : ''}`);
  },
  // ---- 节点汇总(2026-09-03):跨项目摊开看计划节点 ----
  nodeSummary: (query: {
    scope?: NodeSummaryScope; projectId?: number | null; ownerUserId?: number | null;
    /** 按人筛：值来自返回体 byOwner 里的 ownerKey，原样回传，别在这儿拼 */
    ownerKey?: string | null;
    q?: string | null; limit?: number; offset?: number;
  } = {}) => {
    const params = new URLSearchParams();
    if (query.scope) params.set('scope', query.scope);
    if (query.projectId) params.set('projectId', String(query.projectId));
    if (query.ownerUserId) params.set('ownerUserId', String(query.ownerUserId));
    if (query.ownerKey) params.set('ownerKey', query.ownerKey);
    if (query.q?.trim()) params.set('q', query.q.trim());
    if (query.limit) params.set('limit', String(query.limit));
    if (query.offset) params.set('offset', String(query.offset));
    const suffix = params.toString();
    return api.get<NodeSummaryResponse>(`projects/nodes/summary${suffix ? `?${suffix}` : ''}`);
  },
  work: async (): Promise<WorkResponse> => {
    const payload = await api.get<RawWorkbenchResponse>('workbench');
    return {
      asOf: new Date().toISOString(),
      identity: payload.identity,
      counts: payload.counts,
      total: payload.total,
      upcoming: typeof payload.upcoming === 'number' ? payload.upcoming : undefined,
      returned: payload.returned,
      truncated: payload.truncated,
      items: payload.items.map(normalizedWorkItem),
    };
  },
  /** 首页「我负责的交付物」：按项目汇总，点数字带筛选跳到那个项目的交付物清单 */
  myDeliverables: () => api.get<MyDeliverables>('workbench/my-deliverables'),
  /** 侧栏底部「等我处理」的三个数 */
  waitingOnMe: () => api.get<{ approvals: number; reviews: number; tasks: number }>(
    'workbench/waiting-on-me'),
  parseQuickUpdate: (text: string) =>
    api.post<{
      proposals: Array<Record<string, unknown>>;
      unmatched: string[];
      scanned?: number;
      truncated?: boolean;
      note?: string;
    }>('workbench/quick-update/parse', { text }),
  applyQuickUpdate: (
    /** evidence：那段话里支撑这条的原话；没挂交付物的任务标完成时后端拿它当完成说明（0930） */
    updates: Array<{ itemId: string; status: string | null; actualDate: string | null; evidence?: string | null }>,
  ) => api.post<{
    applied: number; failed: number;
    results: Array<{ itemId: string; ok: boolean; message: string }>;
  }>('workbench/quick-update/apply', { updates }),
  updateWorkStatus: (id: string | number, status: string) =>
    api.patch<Record<string, unknown>>(`workbench/${id}/status`, { status }),
  projectList: () => api.get<Project[]>('projects?includeArchived=true'),
  /** 项目总监看板：按人看负荷、按项目看进展 */
  directorOverview: () => api.get<Record<string, unknown>>('projects/director/overview'),
  /** 经营快照趋势（0926）：每天 06:00 拍的，按公司 / 项目经理 / 项目给「较上周」 */
  directorTrends: (weeks = 8) => api.get<SnapshotTrends>(`projects/director/trends?weeks=${weeks}`),
  /** 手工拍一张今天的快照（管理员） */
  captureSnapshot: () => api.post<Record<string, unknown>>('projects/director/snapshots/capture', {}),
  createProject: (payload: ProjectWritePayload) =>
    api.post<Project>('projects', payload),
  /**
   * 「重新铺设计划」：先预览再执行，两个端点。
   *
   * 预览一定要先调——重铺会把一批旧活动标作废，看不到「会动哪些」就点确认
   * 等于让人蒙着眼签字。两个端点用的是后端同一份判据，所以预览给的数就是会发生的事。
   */
  previewPlanRelay: (projectId: string | number) =>
    api.post<PlanRelayPreview>(`projects/${projectId}/plan/relay/preview`, {}),
  previewPlanOwners: (projectId: string | number) =>
    api.get<OwnerAssignmentPreview>(`projects/${projectId}/plan/owners/preview`),
  confirmPlanOwners: (projectId: string | number, assignments: ConfirmOwnerAssignment[]) =>
    api.post<{ assigned: number }>(`projects/${projectId}/plan/owners/confirm`, { assignments }),
  relayPlan: (projectId: string | number) =>
    api.post<PlanRelayResult>(`projects/${projectId}/plan/relay`, {}),
  previewProjectInitialization: (payload: ProjectInitializationRequest) =>
    api.post<ProjectInitializationPreview>('projects/preview', payload),
  initializeProject: (payload: ProjectInitializationRequest) =>
    api.post<ProjectInitializationResult>('projects', payload),
  /** 只送要改的字段就行：后端按「值不是 undefined」判改了哪几项（0922） */
  updateProject: (projectId: string | number, payload: Partial<ProjectWritePayload>) =>
    api.patch<Project>(`projects/${projectId}`, payload),
  advanceProjectStatus: (projectId: string | number, status: ProjectStatus) =>
    api.post<Project>(`projects/${projectId}/status`, { status }),
  projectDetail: async (projectId: string | number) =>
    normalizedProjectDetail(await api.get<Record<string, unknown>>(`projects/${projectId}`)),
  projectPlan: async (projectId: string | number) =>
    normalizedSchedule(await api.get<RawProjectSchedule>(`projects/${projectId}/schedule`)),
  updatePlanNode: (projectId: string | number, nodeId: string | number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`projects/${projectId}/nodes/${nodeId}`, payload),
  createPlanNode: (projectId: string | number, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/nodes`, payload),
  planDependencies: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/dependencies`),
  createPlanDependency: (projectId: string | number, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/dependencies`, payload),
  removePlanDependency: (projectId: string | number, dependencyId: string | number) =>
    api.delete<Record<string, unknown>>(`projects/${projectId}/dependencies/${dependencyId}`),
  previewCascade: (projectId: string | number, changes: Array<{ nodeId: number; end: string | null }>) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/dependencies/cascade`, { changes }),
  planNodeFolders: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/node-folders`),
  setPlanNodeFolder: (
    projectId: string | number,
    nodeId: string | number,
    documentId: number | null,
  ) => api.post<Record<string, unknown>>(
    `projects/${projectId}/nodes/${nodeId}/folder`, { documentId },
  ),
  planAttachments: (projectId: string | number, nodeId?: string | number) =>
    api.get<Record<string, unknown>>(
      `projects/${projectId}/attachments${nodeId === undefined ? '' : `?nodeId=${nodeId}`}`,
    ),
  /** 打包下载交给浏览器直接跳转，不经过 fetch——几百 MB 不该先进内存 */
  planAttachmentsArchiveUrl: (projectId: string | number, nodeId?: string | number) =>
    apiPath(`projects/${encodeURIComponent(String(projectId))}/attachments/archive`)
      + (nodeId === undefined ? '' : `?nodeId=${encodeURIComponent(String(nodeId))}`),
  remindPlanNode: (projectId: string | number, nodeId: string | number, note?: string) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/nodes/${nodeId}/remind`, { note }),
  bom: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/bom`),
  /** 零件图片：列表只知道有没有，图按需从这里取 */
  bomImageUrl: (projectId: string | number, itemId: string | number) =>
    apiPath(`projects/${encodeURIComponent(String(projectId))}/bom/${itemId}/image`),
  uploadBomImage: (projectId: string | number, itemId: string | number, payload: FormData) =>
    api.upload<Record<string, unknown>>(`projects/${projectId}/bom/${itemId}/image`, payload),
  /** 批量改：从 Excel 粘一片区域进来时用，一个事务 */
  bulkUpdateBom: (projectId: string | number, patches: Array<{ id: number; values: Record<string, unknown> }>) =>
    api.patch<Record<string, unknown>>(`projects/${projectId}/bom`, { patches }),
  createBomItem: (projectId: string | number, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/bom`, payload),
  updateBomItem: (projectId: string | number, itemId: string | number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`projects/${projectId}/bom/${itemId}`, payload),
  removeBomItem: (projectId: string | number, itemId: string | number) =>
    api.delete<Record<string, unknown>>(`projects/${projectId}/bom/${itemId}`),
  /** 制造 BOM：按段分组的父子对表 */
  projectMbom: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/mbom`),
  /** 生成前体检：层级缩进错位、缺毛重 */
  inspectMbom: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/mbom/inspect`),
  /** 体检报了错位之后：这一行该挂到哪个总成下（规则打分 + AI 在候选里裁决） */
  adviseBomLevels: (projectId: string | number) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/bom/level-advice`, {}),
  mbomAiState: (projectId: string | number) => api.get<any>(`projects/${projectId}/mbom/ai-review`),
  startMbomAi: (projectId: string | number) => api.post<any>(`projects/${projectId}/mbom/ai-review`, {}),
  decideMbomAi: (projectId: string | number, payload: Record<string, unknown>) => api.post<any>(`projects/${projectId}/mbom/ai-review/decision`, payload),
  decideMbomBlank: (projectId: string | number, reviewId: string, payload: Record<string, unknown>) => api.post<any>(`projects/${projectId}/mbom/ai-review/blank/${reviewId}`, payload),
  generateMbom: (projectId: string | number) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/mbom/generate`, {}),
  /** EBOM 改了之后 MBOM 哪里没跟上 */
  mbomImpact: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/mbom/impact`),
  /** MBOM 就地改：只开放用量、组织、损耗、备注 */
  updateMbomRow: (projectId: string | number, itemId: string | number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`projects/${projectId}/mbom/${itemId}`, payload),
  bomReleases: (projectId: string | number, type: 'ebom' | 'mbom') =>
    api.get<Record<string, unknown>>(`projects/${projectId}/bom/releases?type=${type}`),
  releaseBom: (projectId: string | number, payload: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/bom/releases`, payload),
  /* 量产 BOM 台账（1030）：移交后的 BOM 入口、接收名单、同步回执 */
  bomLedger: () => api.get<{ items: BomLedgerRow[] }>('bom-ledger'),
  bomLedgerSummary: () => api.get<BomLedgerSummary>('bom-ledger/summary'),
  bomLedgerDetail: (projectId: string | number) =>
    api.get<BomLedgerDetail>(`bom-ledger/${projectId}`),
  addBomReceiver: (projectId: string | number, payload: { userId: number; duty: string }) =>
    api.post<{ joinedTeam: boolean }>(`bom-ledger/${projectId}/receivers`, payload),
  removeBomReceiver: (projectId: string | number, payload: { userId: number; duty: string }) =>
    api.post<{ removed: boolean }>(`bom-ledger/${projectId}/receivers/remove`, payload),
  syncBomRelease: (projectId: string | number, releaseId: number) =>
    api.post<BomSyncResult>(`bom-ledger/${projectId}/releases/${releaseId}/sync`, {}),
  confirmBomReceipt: (receiptId: number, note: string | null) =>
    api.post<{ id: number; confirmedAt: string; note: string | null }>(
      `bom-ledger/receipts/${receiptId}/confirm`, { note }),
  mbomExportUrl: (projectId: string | number) =>
    apiPath(`projects/${encodeURIComponent(String(projectId))}/mbom/export`),
  bomExportUrl: (projectId: string | number) =>
    apiPath(`projects/${encodeURIComponent(String(projectId))}/bom/export`),
  bomTemplateUrl: (projectId: string | number) =>
    apiPath(`projects/${encodeURIComponent(String(projectId))}/bom/template`),
  previewBomImport: (projectId: string | number, payload: FormData, sheet?: string) =>
    api.upload<Record<string, unknown>>(
      `projects/${encodeURIComponent(String(projectId))}/bom/import/preview`
        + `${sheet ? `?sheet=${encodeURIComponent(sheet)}` : ''}`,
      payload,
    ),
  confirmBomImport: (
    projectId: string | number,
    items: Array<Record<string, unknown>>,
    // 预览时算出的表头映射，原样带回去存档（0821）
    source?: Record<string, unknown> | null,
    // Excel 里那张「更改履历表」，一并存档（0822 第 3 条）
    revisions?: Array<Record<string, unknown>> | null,
  ) =>
    api.post<Record<string, unknown>>(
      `projects/${projectId}/bom/import/confirm`, { items, source, revisions },
    ),
  deletePlanNode: (projectId: string | number, nodeId: string | number) =>
    api.delete<Record<string, unknown>>(`projects/${projectId}/nodes/${nodeId}`),
  updateMilestone: (
    projectId: string | number,
    milestoneId: string | number,
    payload: { planDate?: string | null; actualDate?: string | null; remark?: string | null },
  ) => api.patch<Record<string, unknown>>(`projects/${projectId}/milestones/${milestoneId}`, payload),
  pfmeaWorksheetTemplateUrl: () => apiPath('module-tools/pfmea-worksheet-template'),
  bomChanges: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/bom/changes`),
  setBomChangeParts: (projectId: string | number, changeId: string | number, itemIds: number[]) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/bom/changes/${changeId}`, { itemIds }),
  previewBomAutoLink: (projectId: string | number) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/bom/auto-link/preview`, {}),
  applyBomAutoLink: (
    projectId: string | number,
    links: Array<{ itemId: number; slot: string; documentId: number }>,
  ) => api.post<Record<string, unknown>>(`projects/${projectId}/bom/auto-link/apply`, { links }),
  nodeReviewBrief: (projectId: string | number, nodeId: string | number, refresh = false) =>
    api.post<Record<string, unknown>>(
      `projects/${projectId}/nodes/${nodeId}/review-brief${refresh ? '?refresh=1' : ''}`, {},
    ),
  /** 智能立单：可以只贴文字、只传截图/表格，也可以两个都给 */
  assistIssueIntake: (projectId: string | number, text: string, file?: File | null) => {
    const form = new FormData();
    form.append('text', text);
    if (file) form.append('file', file);
    // 必须走 upload：post 会把 FormData 再 JSON 序列化一遍，文件就没了
    return api.upload<Record<string, unknown>>(`projects/${projectId}/issues/assist`, form);
  },
  nodeReviewers: (projectId: string | number, nodeId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/nodes/${nodeId}/reviewers`),
  decideNodeReview: (
    projectId: string | number,
    nodeId: string | number,
    reviewerId: string | number,
    payload: { conclusion: 'approved' | 'rejected'; comment?: string },
  ) => api.post<Record<string, unknown>>(
    `projects/${projectId}/nodes/${nodeId}/reviewers/${reviewerId}/decision`, payload,
  ),
  addNodeReviewer: (
    projectId: string | number,
    nodeId: string | number,
    payload: { userId: number; stepOrder?: number },
  ) => api.post<Record<string, unknown>>(`projects/${projectId}/nodes/${nodeId}/reviewers`, payload),
  excludeNodeReviewer: (
    projectId: string | number,
    nodeId: string | number,
    reviewerId: string | number,
  ) => api.delete<Record<string, unknown>>(`projects/${projectId}/nodes/${nodeId}/reviewers/${reviewerId}`),
  ppap: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/ppap`),
  createPpap: (projectId: string | number, payload: { submissionLevel?: number }) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/ppap`, payload),
  updatePpap: (projectId: string | number, payload: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`projects/${projectId}/ppap`, payload),
  updatePpapElement: (
    projectId: string | number,
    elementId: string | number,
    payload: Record<string, unknown>,
  ) => api.patch<Record<string, unknown>>(`projects/${projectId}/ppap/elements/${elementId}`, payload),
  issueAttachments: (projectId: string | number, issueId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/issues/${issueId}/attachments`),
  issueAttachmentUrl: (projectId: string | number, issueId: string | number, index: number) =>
    apiPath(`projects/${encodeURIComponent(String(projectId))}/issues/${encodeURIComponent(String(issueId))}/attachments/${index}`),
  people: () => api.get<Record<string, unknown>>('people'),
  auditEvents: (params: Record<string, string | number | undefined>) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') search.set(key, String(value));
    }
    return api.get<Record<string, unknown>>(`audit?${search.toString()}`);
  },
  /** 筛选项的条数要跟着当前条件走，不然会出现「下拉写着 28 条、点进去 0 条」 */
  auditFacets: (params: Record<string, string | number | undefined> = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') search.set(key, String(value));
    }
    const query = search.toString();
    return api.get<Record<string, unknown>>(`audit/facets${query ? `?${query}` : ''}`);
  },
  /** 概览：谁在动、哪几条被动得最多、每天多少条（0824） */
  auditSummary: (days = 30) =>
    api.get<Record<string, unknown>>(`audit/summary?days=${days}`),
  recordHistory: (entityType: string, entityId: string | number) =>
    api.get<Record<string, unknown>>(`audit/${entityType}/${entityId}`),
  parseSchedule: (projectId: string | number, text: string) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/schedule/ai/parse`, { text }),
  batchUpdatePlanNodes: (
    projectId: string | number,
    updates: Array<Record<string, unknown>>,
  ) => api.patch<Record<string, unknown>>(`projects/${projectId}/nodes/batch`, { updates }),
  planWorkbookUrl: (projectId: string | number) =>
    apiPath(`projects/${encodeURIComponent(String(projectId))}/schedule/export`),
  previewPlanImport: (projectId: string | number, payload: FormData) =>
    api.upload<PlanImportPreview>(
      `projects/${encodeURIComponent(String(projectId))}/schedule/import/preview`,
      payload,
    ),
  confirmPlanImport: (
    projectId: string | number,
    updates: Array<Record<string, unknown>>,
    creations: Array<Record<string, unknown>> = [],
  ) => api.post<Record<string, unknown>>(
    `projects/${encodeURIComponent(String(projectId))}/schedule/import/confirm`,
    { updates, creations },
  ),
  pfmeaTree: (projectId: string | number, documentId: string | number) =>
    api.get<FmeaTreeResponse>(`projects/${projectId}/pfmea/${documentId}/tree`),
  previewPfmeaImport: (
    projectId: string | number, documentId: string | number, payload: FormData,
  ) => api.upload<PfmeaImportPreview>(
    `projects/${projectId}/pfmea/${documentId}/import/preview`, payload),
  /** 计划节点的进展留痕 */
  nodeProgress: (projectId: string | number, nodeId: string | number) =>
    api.get<{ nodeId: number; events: Array<Record<string, unknown>> }>(
      `projects/${projectId}/nodes/${nodeId}/progress`,
    ),
  /** 项目总览大屏：整屏数据一次带回 */
  bigScreen: () => api.get<Record<string, unknown>>('dashboard/big-screen'),
  /** 交付物配对：没挂文件的交付物 + 未认领的真实文件 */
  deliverablePairing: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/deliverables/pairing`),
  /** 这个项目还缺什么（概览卡片） */
  projectReadiness: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/readiness`),
  /** 这个节点实际会怎么流转（画流程图用） */
  approvalRoute: (projectId: string | number, nodeId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/nodes/${nodeId}/approval-route`),
  /** 交付物这一单的真实路径（含谁批过），和按节点那条不是一回事 */
  deliverableApprovalRoute: (projectId: string | number, deliverableId: string | number) =>
    api.get<Record<string, unknown>>(
      `projects/${projectId}/deliverables/${deliverableId}/approval-route`),
  /** 工作日历：这段日期里的法定假日和调休上班日（周末前端自己判），排期时提示用（1006） */
  calendarEntries: (from: string, to: string) =>
    api.get<{ entries: Array<{ day: string; kind: 'holiday' | 'workday'; name: string }> }>(
      `calendar/entries?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  /** 项目角色选项：以审批流里的角色为准 */
  projectRoles: () =>
    api.get<{ roles: Array<Record<string, unknown>>; approvalCount: number }>('config-view/project-roles'),
  /** 移出前开放事项（硬闸 + 软提示） */
  teamMemberOpenItems: (projectId: string | number, memberId: string | number) =>
    api.get<{
      member: { id: number; projectId: number; userId: number | null; name: string; roleTitle: string | null };
      hard: Array<{ kind: string; gate: string; id: number; title: string; status: string | null; dueAt: string | null; projectId: number; match: string }>;
      soft: Array<{ kind: string; gate: string; id: number; title: string; status: string | null; dueAt: string | null; projectId: number; match: string }>;
      canRemove: boolean;
    }>(`projects/${projectId}/team/members/${memberId}/open-items`),
  /** 交接改派硬闸事项；目标不在团队则自动入队 */
  teamMemberReassign: (
    projectId: string | number,
    memberId: string | number,
    payload: { items: Array<{ kind: string; id: number; toUserId: number }>; addRoleTitle?: string },
  ) =>
    api.post<{ reassigned: number; addedMember: boolean; remainingHard: number; canRemove: boolean }>(
      `projects/${projectId}/team/members/${memberId}/reassign`, payload),
  /** 把成员移出项目（真删除，带审计；服务端硬闸未交接则 403） */
  removeProjectMember: (memberId: string | number) =>
    api.delete<{ deleted: boolean }>(`modules/hr/project-members/${memberId}`),
  /** 按现在的项目团队重新派发当前这一级的审批待办（0905：待办是提交时定格的，团队会变） */
  reassignDeliverableApproval: (projectId: string | number, deliverableId: string | number) =>
    api.post<{ step: number; added: string[]; removed: string[]; kept: string[] }>(
      `projects/${projectId}/deliverables/${deliverableId}/approval/reassign`, {}),
  /** 团队页：本项目审批流点名、但团队里没人担的角色 */
  teamVacancies: (projectId: string | number) =>
    api.get<TeamVacancies>(`projects/${projectId}/team/vacancies`),
  /** AI 追问 5Why：只补空位，已填的原样带回 */
  fiveWhyAssist: (payload: { title?: string; description?: string; whys?: string[]; rootCause?: string }) =>
    api.post<{ whys: string[]; rootCause: string }>('modules/issues/five-why-assist', payload),
  /** 参考历史案例给根因/措施草稿；案例正文由后端原样带回，AI 只挑不改 */
  issueSolutionAssist: (payload: {
    issueId?: number; title?: string; description?: string;
    partName?: string; productCategory?: string; category?: string;
  }) => api.post<Record<string, unknown>>('modules/issues/solution-assist', payload),
  /** 8D 报告看板：有 8D 步骤的问题 + 各 D 步进度 */
  /** 把一条问题转成 8D：建一套空的 D0–D8（幂等，已有的不动） */
  startIssue8d: (issueId: string | number) =>
    api.post<{ issueId: number; created: number; alreadyStarted: boolean }>(
      `modules/issues/${issueId}/8d`, {}),
  /** 变更批准后一键铺标准执行清单（图纸/BOM/工装/PFMEA/断点/PPAP…），幂等 */
  startChangeChecklist: (changeId: string | number) =>
    api.post<{ changeId: number; created: number; alreadyStarted: boolean }>(
      `modules/changes/${changeId}/checklist`, {}),
  /*
    这里原来有 changeChecklistPreview（勾影响范围时预览会生成哪几条）。
    0826 通用字段区删掉后 impactScopes 勾选框不再渲染，预览组件跟着退役；
    后端 checklist-preview 路由留着不拆——真正的生成逻辑和它共用同一份裁剪函数。
    0908 补记：组件文件 ChangeChecklistPreview.vue 当时漏删，在服务器源码树上又躺了三周；
    它调的就是上面这个已删的方法，vue-tsc 报 TS2339，整个 npm run build 直接挂（没人 import 它，
    所以只有跑完整 build 的人会撞上）。今天已删，备份在 deploy/backups/code-20260908T184412/。
  */
  /** 催办这条变更下所有未完成措施；返回催到几个、谁催不到 */
  remindChangeActions: (changeId: string | number) =>
    api.post<{ pending: number; notified: number; noOwner: string[]; unresolved: string[] }>(
      `modules/changes/${changeId}/remind-actions`, {}),
  issue8dBoard: () =>
    api.get<{ items: Array<Record<string, unknown>> }>('modules/issues/8d-board'),
  /** 配置只读查看：审批流与字典 */
  approvalWorkflows: () =>
    api.get<{ total: number; stepless: number; workflows: Array<Record<string, unknown>> }>('config-view/approval-workflows'),
  configDictionaries: () =>
    api.get<{ total: number; groups: Array<Record<string, unknown>> }>('config-view/dictionaries'),
  /* ---------- 站内通知 ---------- */
  notifications: (unreadOnly = false, limit = 50) =>
    api.get<{ unread: number; items: AppNotification[] }>(
      `notifications?unreadOnly=${unreadOnly}&limit=${limit}`,
    ),
  markNotificationsRead: (ids: number[]) =>
    api.patch<{ updated: number }>('notifications/read', { ids }),
  markAllNotificationsRead: () =>
    api.post<{ updated: number }>('notifications/read-all', {}),
  /** 按零件名称把 PFMEA 挂到 BOM 顶层零件 */
  planPfmeaBomLink: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/pfmea/bom-link/plan`),
  applyPfmeaBomLink: (projectId: string | number) =>
    api.post<{ linked: number; results?: Array<Record<string, unknown>>; note?: string }>(
      `projects/${projectId}/pfmea/bom-link/apply`, {},
    ),
  /** 整簿批量导入：一次把工作簿里的 N 张表建成 N 条 PFMEA */
  batchPreviewPfmeaImport: (projectId: string | number, payload: FormData) =>
    api.upload<PfmeaBatchPreview>(`projects/${projectId}/pfmea/import/batch/preview`, payload),
  batchConfirmPfmeaImport: (
    projectId: string | number,
    sheets: Array<Record<string, unknown>>,
    fileName?: string,
  ) => api.post<{ documents: number; imported: number; results: Array<Record<string, unknown>> }>(
    `projects/${projectId}/pfmea/import/batch/confirm`, { sheets, fileName },
  ),
  confirmPfmeaImport: (
    projectId: string | number, documentId: string | number,
    records: Array<Record<string, unknown>>, replaceExisting: boolean,
    source: { fileName?: string; sheet?: string } = {},
  ) => api.post<{ imported: number; sourceRows: number; replaced: boolean; tree: FmeaTreeResponse }>(
    `projects/${projectId}/pfmea/${documentId}/import/confirm`,
    { records, replaceExisting, ...source },
  ),
  fmeaAiStatus: (projectId: string | number) =>
    api.get<{ configured: boolean; model: string }>(`projects/${projectId}/pfmea/ai/status`),
  generateFmeaFailures: (
    projectId: string | number,
    payload: Record<string, unknown>,
  ) => api.post<{ proposal: unknown[]; warnings: string[] }>(
    `projects/${projectId}/pfmea/ai/failures`, payload),
  adoptFmeaChains: (
    projectId: string | number, documentId: string | number,
    payload: { functionId: number; chains: unknown[] },
  ) => api.post<FmeaTreeResponse & { adopted?: number }>(
    `projects/${projectId}/pfmea/${documentId}/adopt`, payload),
  generateControlPlanFromPfmea: (
    projectId: string | number, documentId: string | number, subject?: string,
  ) => api.post<{ proposal: Record<string, string>[]; warnings: string[] }>(
    `projects/${projectId}/pfmea/${documentId}/ai/control-plan`,
    subject ? { subject } : {}),
  adoptControlPlan: (
    projectId: string | number,
    payload: { controlPlanId?: number | null; title: string; lines: Record<string, string>[] },
  ) => api.post<{ controlPlanId: number; adopted: number }>(
    `projects/${projectId}/control-plans/adopt`, payload),
  specialCharacteristics: (projectId: string | number) =>
    api.get<SpecialCharacteristicsResponse>(`projects/${projectId}/special-characteristics`),
  /* ---------- 图纸与特性库（气泡图） ---------- */
  projectDrawings: (projectId: string | number) =>
    api.get<{ items: PartDrawing[] }>(`projects/${projectId}/drawings`),
  uploadDrawing: (projectId: string | number, payload: FormData) =>
    api.upload<{ id: number }>(`projects/${projectId}/drawings`, payload),
  drawingDetail: (projectId: string | number, drawingId: string | number) =>
    api.get<PartDrawingDetail>(`projects/${projectId}/drawings/${drawingId}`),
  deleteDrawing: (projectId: string | number, drawingId: string | number) =>
    api.delete<{ removed: boolean }>(`projects/${projectId}/drawings/${drawingId}`),
  drawingAssetUrl: (projectId: string | number, drawingId: string | number, kind: string, tile?: number) =>
    apiPath(`projects/${projectId}/drawings/${drawingId}/asset?kind=${kind}${tile === undefined ? '' : `&tile=${tile}`}`),
  /** fresh：不复用同一份 PDF 上次的识别结果，真跑一遍 AI（1006） */
  startDrawingExtract: (projectId: string | number, drawingId: string | number, options: { fresh?: boolean } = {}) =>
    api.post<{ started: boolean; reused?: boolean; from?: number; merged?: number }>(
      `projects/${projectId}/drawings/${drawingId}/extract${options.fresh ? '?fresh=1' : ''}`, {}),
  /** 批量删特性（1006「列表多选」）：一个事务删完、号只补一次、留痕只写一条 */
  bulkDeleteDrawingCharacteristics: (
    projectId: string | number, drawingId: string | number, ids: number[],
  ) => api.post<{ removed: number; renumbered: number; locked: boolean; droppedMeasurements: number }>(
    `projects/${projectId}/drawings/${drawingId}/characteristics/bulk-delete`, { ids }),
  /** 气泡号留空时由后端接在最后定号，回来的 balloonNo 就是最终定下的那个 */
  createDrawingCharacteristic: (
    projectId: string | number, drawingId: string | number, payload: Record<string, unknown>,
  ) => api.post<{ id: number; balloonNo?: string; reportLocked?: boolean }>(
    `projects/${projectId}/drawings/${drawingId}/characteristics`, payload),
  updateDrawingCharacteristic: (
    projectId: string | number, drawingId: string | number, characteristicId: number,
    payload: Record<string, unknown>,
  ) => api.patch<{ id: number; status: string }>(
    `projects/${projectId}/drawings/${drawingId}/characteristics/${characteristicId}`, payload),
  deleteDrawingCharacteristic: (
    projectId: string | number, drawingId: string | number, characteristicId: number,
  ) => api.delete<{
    removed: boolean; renumbered: number; locked: boolean; droppedMeasurements: number;
  }>(`projects/${projectId}/drawings/${drawingId}/characteristics/${characteristicId}`),
  renumberDrawing: (projectId: string | number, drawingId: string | number) =>
    api.post<{ count: number }>(`projects/${projectId}/drawings/${drawingId}/renumber`, {}),
  /* ---------- 全尺寸检验报告 ---------- */
  dimensionalReports: (projectId: string | number, drawingId: string | number) =>
    api.get<{ items: DimensionalReportSummaryItem[] }>(
      `projects/${projectId}/drawings/${drawingId}/dimensional-reports`),
  createDimensionalReport: (
    projectId: string | number, drawingId: string | number, payload: Record<string, unknown>,
  ) => api.post<{ id: number; code: string }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports`, payload),
  dimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => api.get<DimensionalReportDetail>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}`),
  updateDimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number,
    payload: Record<string, unknown>,
  ) => api.patch<{ id: number }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}`, payload),
  deleteDimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => api.delete<{ ok: true }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}`),
  saveDimensionalMeasurements: (
    projectId: string | number, drawingId: string | number, reportId: number,
    items: Array<{ characteristicId: number; sampleNo: number; value: number | null }>,
  ) => api.post<{ saved: number }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/measurements`,
    { items }),
  /* 两级审批：提交 / 审核 / 批准发布 / 退回 */
  submitDimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => api.post<{ status: string }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/submit`, {}),
  /** 提交人自己收回，只在还没人审的时候；和审批人「退回」是两回事 */
  withdrawDimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => api.post<{ status: string }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/withdraw`, {}),
  reviewDimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => api.post<{ status: string }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/review`, {}),
  releaseDimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => api.post<{ status: string }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/release`, {}),
  rejectDimensionalReport: (
    projectId: string | number, drawingId: string | number, reportId: number, reason: string,
  ) => api.post<{ status: string }>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/reject`, { reason }),

  /** 导出前的版式预览：和 /export 生成 xlsx 用的是同一份模型 */
  dimensionalReportPreview: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => api.get<DimensionalSheetModel>(
    `projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/preview`),
  dimensionalReportExportUrl: (
    projectId: string | number, drawingId: string | number, reportId: number,
  ) => apiPath(`projects/${projectId}/drawings/${drawingId}/dimensional-reports/${reportId}/export`),
  createFmeaItem: (
    projectId: string | number, documentId: string | number,
    payload: { kind: string; parentId: number | null; title: string; failureType?: string },
  ) => api.post<FmeaTreeResponse>(`projects/${projectId}/pfmea/${documentId}/items`, payload),
  updateFmeaItem: (
    projectId: string | number, documentId: string | number, itemId: number,
    payload: Record<string, unknown>,
  ) => api.patch<FmeaTreeResponse>(`projects/${projectId}/pfmea/${documentId}/items/${itemId}`, payload),
  deleteFmeaItem: (
    projectId: string | number, documentId: string | number, itemId: number,
  ) => api.delete<FmeaTreeResponse>(`projects/${projectId}/pfmea/${documentId}/items/${itemId}`),
  projectDeliverables: (projectId: string | number) =>
    api.get<Deliverable[]>(`projects/${projectId}/deliverables`),
  createProjectDeliverable: (
    projectId: string | number,
    payload: DeliverableWritePayload,
  ) => api.post<Deliverable>(`projects/${projectId}/deliverables`, payload),
  /** 后端按字段增量更新（has('documentId')），所以允许只传要改的那几个 */
  updateProjectDeliverable: (
    projectId: string | number,
    deliverableId: string | number,
    payload: Partial<DeliverableWritePayload>,
  ) => api.patch<Deliverable>(
    `projects/${projectId}/deliverables/${deliverableId}`,
    payload,
  ),
  attachDeliverableFile: (
    projectId: string | number,
    deliverableId: string | number,
    payload: FormData,
  ) => api.upload<Deliverable>(
    `projects/${projectId}/deliverables/${deliverableId}/file`,
    payload,
  ),
  decideProjectDeliverable: (
    projectId: string | number,
    deliverableId: string | number,
    decision: 'approved' | 'rejected',
    comment?: string,
  ) => api.post<Deliverable>(
    `projects/${projectId}/deliverables/${deliverableId}/decide`,
    { decision, ...(comment?.trim() ? { comment: comment.trim() } : {}) },
  ),
  submitProjectDeliverable: (
    projectId: string | number,
    deliverableId: string | number,
    /** 留空则由后端按审批流步骤角色解析项目成员 */
    approverUserId?: number,
    approverName?: string,
  ) => api.post<Deliverable>(
    `projects/${projectId}/deliverables/${deliverableId}/submit`,
    {
      ...(approverUserId ? { approverUserId } : {}),
      ...(approverName?.trim() ? { approverName: approverName.trim() } : {}),
    },
  ),
  advanceProjectDeliverableStatus: (
    projectId: string | number,
    deliverableId: string | number,
    status: DeliverableStatus,
  ) => api.post<Deliverable>(
    `projects/${projectId}/deliverables/${deliverableId}/status`,
    { status },
  ),
  moduleCatalog: () => api.get<ModuleCatalogItem[]>('modules'),
  moduleRecords: async (
    module: string, resource: string,
    mode: 'resource' | 'projects' | 'dashboard' = 'resource',
    options: { includeArchived?: boolean } = {},
  ): Promise<ModuleRecordsResponse> => {
    if (mode === 'projects') {
      /*
       * 已删除和已归档的项目只有管理员和总监看得到（0902 Tony）。
       * 这里按角色决定要不要，后端还会再拦一道——前端这层只是别让普通人
       * 白等一次注定被过滤掉的请求，真正把关的是 projects.controller。
       */
      const query = options.includeArchived ? '?includeArchived=true' : '';
      const items = await api.get<Project[]>(`projects${query}`);
      return { items: items as unknown as ModuleRecord[], total: items.length };
    }
    if (mode === 'dashboard') {
      const dashboard = await api.get<Record<string, unknown>>('dashboard');
      const items = Array.isArray(dashboard.exceptions) ? dashboard.exceptions as ModuleRecord[] : [];
      // total 要用后端给的真实总数：items 只是前 20 条，拿它当总数会谎报
      const total = typeof dashboard.exceptionTotal === 'number'
        ? dashboard.exceptionTotal
        : items.length;
      return { items, total };
    }
    const payload = await api.get<{ items: ModuleRecord[] }>(`modules/${module}/${resource}`);
    return { items: payload.items, total: payload.items.length };
  },
  createModuleRecord: (module: string, resource: string, payload: Record<string, unknown>) =>
    api.post<ModuleRecord>(`modules/${module}/${resource}`, payload),
  updateModuleRecord: (module: string, resource: string, recordId: string | number, payload: Record<string, unknown>) =>
    api.patch<ModuleRecord>(`modules/${module}/${resource}/${recordId}`, payload),
  updateModuleStatus: (module: string, resource: string, recordId: string | number, status: string) =>
    api.post<ModuleRecord>(`modules/${module}/${resource}/${recordId}/status`, { status }),
  /** 催办：给记录责任人推站内+邮件提醒 */
  remindModuleRecord: (module: string, resource: string, recordId: string | number) =>
    api.post<{ notified: number; ownerName: string | null; channels?: string[]; skipped: string | null }>(
      `modules/${module}/${resource}/${recordId}/remind`, {},
    ),
  /**
   * 项目子页的记录列表：翻页取全。
   *
   * 原来不传条数，吃后端默认的 50——秦岭OP3 的文件页有 346 个文件，
   * 界面上只出现 50 个，剩下 296 个既不报错也没有提示，
   * 看上去就像「系统里没有」。列表少东西比列表报错难发现得多。
   */
  projectResourceRecords: (module: string, resource: string, projectId: string | number) =>
    pagedRecords(`modules/${module}/${resource}?projectId=${encodeURIComponent(String(projectId))}`),
  /** 子表记录（控制计划的控制行、8D 步骤等）：按父记录取全 */
  childRecords: (module: string, resource: string, parentId: string | number) =>
    pagedRecords(`modules/${module}/${resource}?parentId=${encodeURIComponent(String(parentId))}`),
  createProjectResource: (module: string, resource: string, projectId: string | number, payload: Record<string, unknown>) =>
    api.post<ModuleRecord>(`modules/${module}/${resource}`, { ...payload, projectId: Number(projectId) }),
  updateProjectResource: (module: string, resource: string, itemId: string | number, payload: Record<string, unknown>) =>
    api.patch<ModuleRecord>(`modules/${module}/${resource}/${itemId}`, payload),
  advanceProjectResource: (module: string, resource: string, itemId: string | number, status: string) =>
    api.post<ModuleRecord>(`modules/${module}/${resource}/${itemId}/status`, { status }),
  /** 项目文件的目录视图：面包屑 + 左侧树 + 当前这一层 */
  projectFiles: (projectId: string | number, folderId?: string | number | null) =>
    api.get<Record<string, unknown>>(
      `projects/${projectId}/files${folderId ? `?folderId=${folderId}` : ''}`),
  createProjectFolder: (projectId: string | number, parentId: number | null, name: string) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/files/folders`, { parentId, name }),
  uploadProjectFile: (projectId: string | number, payload: FormData) =>
    api.upload<Record<string, unknown>>(`projects/${projectId}/files/upload`, payload),
  /** 哪些节点看着做完了只是没人填完成日期 */
  adviseCompletion: (projectId: string | number) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/plan/completion-advice`, {}),
  confirmCompletion: (projectId: string | number, items: Array<{ nodeId: number; date: string }>) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/plan/completion-confirm`, { items }),
  /** 打包下载一个文件夹（整个阶段一次拿走） */
  projectFilesExportUrl: (projectId: string | number, folderId?: number | null) =>
    apiPath(`projects/${projectId}/files/export${folderId ? `?folderId=${folderId}` : ''}`),
  /** NAS 主存状态：可达性/容量/待整理/暂存文件数（文件管理页状态条用） */
  storageStatus: () => api.get<StorageStatus>('documents/storage-status'),
  // deep=true 会真的打开文件读内容再判，慢得多，由界面上的开关控制
  /** 某条变更的五步 + 每步任务清单 */
  /** 五步的静态定义：新建变更时还没有 ID，用它把五步条摆出来（0824） */
  /**
   * 变更台账的扩展列（0826）：附件数、申请/关闭日期 + 费用列可见性。
   * 行数据仍走通用列表（那里已按权限裁掉费用键），这里只补台账要多显示的几样。
   */
  changeLedgerExtras: (projectId?: number | string | null) =>
    api.get<{
      costVisible: boolean;
      items: Array<{
        id: number; attachmentCount: number;
        appliedAt: string | null; closedAt: string | null;
        /** 这一行的费用当前账号有没有权限（模块中心的 PM 只对自己项目的行有） */
        costAllowed: boolean;
      }>;
    }>(`module-tools/changes/ledger-extras${projectId ? `?projectId=${encodeURIComponent(String(projectId))}` : ''}`),
  changeStepDefinitions: () =>
    api.get<Record<string, unknown>>('module-tools/changes/steps'),

  changeStepOverview: (changeId: string | number) =>
    api.get<Record<string, unknown>>(`module-tools/changes/${changeId}/steps`),
  /** 一键把程序文件里点到的事项铺成这一步的任务 */
  seedChangeStep: (changeId: string | number, step: number) =>
    api.post<Record<string, unknown>>(`module-tools/changes/${changeId}/steps/${step}/seed`, {}),
  addChangeTask: (changeId: string | number, body: Record<string, unknown>) =>
    api.post<Record<string, unknown>>(`module-tools/changes/${changeId}/tasks`, body),
  updateChangeTask: (changeId: string | number, taskId: number, body: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`module-tools/changes/${changeId}/tasks/${taskId}`, body),
  removeChangeTask: (changeId: string | number, taskId: number) =>
    api.delete<Record<string, unknown>>(`module-tools/changes/${changeId}/tasks/${taskId}`),
  /** 推进到下一步：这一步铺了任务就必须勾完 */
  /* 提前关闭（中途关闭）：门禁不拦，但要写原因，并存下当时没做完的东西（0828） */
  closeChangeEarly: (changeId: number, reason: string) =>
    api.post<{ currentStep: number; status: string; unfinished: number }>(
      `module-tools/changes/${changeId}/close-early`, { reason }),
  advanceChangeStep: (changeId: string | number) =>
    api.post<Record<string, unknown>>(`module-tools/changes/${changeId}/advance`, {}),
  /** 变更零件的库存清点表（六行固定，后端第一次读时铺好） */
  /** 驳回一级审批：整条变更驳回并存档（理由必填） */
  rejectChangeApproval: (changeId: string | number, taskId: string | number, reason: string) =>
    api.post<Record<string, unknown>>(`module-tools/changes/${changeId}/tasks/${taskId}/reject`, { reason }),
  changeInventory: (changeId: string | number) =>
    api.get<Record<string, unknown>>(`module-tools/changes/${changeId}/inventory`),
  updateChangeInventory: (changeId: string | number, rowId: string | number, body: Record<string, unknown>) =>
    api.patch<Record<string, unknown>>(`module-tools/changes/${changeId}/inventory/${rowId}`, body),
  /** BOM 更改履历：不用再下载 Excel 才能看改过什么（0822 第 3 条） */
  bomRevisions: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/bom/revisions`),
  /*
    在系统里改一格留下的痕迹（0823 第 1 条）。和上面那条分开：
    上面是 Excel 带来的版次表，重导整表替换；这份只增不改，重导也不能冲掉。
  */
  bomEdits: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/bom/edits`),
  /*
    只把 Excel 里那张「更改履历表」导进来，不动 BOM 行。
    老项目（X04C 的 411 行是迁进来的）没走过带履历解析的导入口，那张表是空的；
    整份重导又会把人工改过的 153 行一起冲掉。
  */
  importBomRevisions: (projectId: string | number, revisions: Array<Record<string, unknown>>) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/bom/revisions`, { revisions }),
  /*
    历次导入的识别记录。记录一直在写，但一直没有接口能读——
    「这一列为什么是空的」只能靠翻当时那份 Excel 回答，而 Excel 早被下一版覆盖了。
  */
  bomImportLogs: (projectId: string | number) =>
    api.get<Record<string, unknown>>(`projects/${projectId}/bom/import-logs`),
  // 系统使用情况（管理员）：谁在用、用哪个模块、哪些人还没进来过
  /*
    组织同步（钉钉 → PDM）。接口一直都在，但界面上从来没有入口——
    Tony 0822：「模块中心的组织没找到」。dryRun 先看清单再决定要不要真跑。
  */
  orgSyncStatus: () => api.get<Record<string, unknown>>('org-sync/status'),
  /**
   * 组织同步。includeDeptPaths 点名的部门（含子部门）整支拉进来**并建账号**——
   * 不点名时行为和以前一模一样（只认已有账号的人，一个号都不建）。
   */
  runOrgSync: (dryRun: boolean, includeDeptPaths: string[] = []) =>
    api.post<Record<string, unknown>>('org-sync/run', { dryRun, includeDeptPaths }),
  usageStats: (days = 30) =>
    api.get<Record<string, unknown>>(`admin/users/usage?days=${days}`),
  adviseFiling: (projectId: string | number, deep = false) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/files/filing-advice`, { deep }),
  applyFiling: (
    projectId: string | number,
    // folderId 为 null 表示目标目录还不存在，后端按 createFolderName 现建（只认标准目录名）
    moves: Array<{ itemId: number; folderId: number | null; createFolderName?: string }>,
  ) =>
    api.post<Record<string, unknown>>(`projects/${projectId}/files/filing-apply`, { moves }),
  renameProjectFile: (projectId: string | number, itemId: number, name: string) =>
    api.patch<Record<string, unknown>>(`projects/${projectId}/files/${itemId}`, { name }),
  moveProjectFile: (projectId: string | number, itemId: number, parentId: number) =>
    api.patch<Record<string, unknown>>(`projects/${projectId}/files/${itemId}`, { parentId }),
  removeProjectFile: (projectId: string | number, itemId: number, force = false) =>
    api.delete<Record<string, unknown>>(
      `projects/${projectId}/files/${itemId}${force ? '?force=true' : ''}`),
  fileVersions: (recordId: string | number) =>
    api.get<{ documentId: number; versions: ModuleRecord[] }>(`documents/${recordId}/versions`),
  uploadFileVersion: (recordId: string | number, payload: FormData) =>
    api.upload<ModuleRecord>(`documents/${recordId}/versions`, payload),
  fileDownloadUrl: (recordId: string | number, versionId: string | number) =>
    apiPath(`documents/${recordId}/versions/${versionId}/download`),
  onlyOfficeStatus: (recordId: string | number) =>
    api.get<{
      configured: boolean;
      missingConfiguration: string[];
      editable: boolean;
      latestVersion: ModuleRecord | null;
      documentServerUrl: string | null;
    }>(`documents/${recordId}/onlyoffice/status`),
  onlyOfficeConfig: (recordId: string | number) =>
    api.get<{ documentServerUrl: string; config: Record<string, unknown> }>(`documents/${recordId}/onlyoffice/config`),
  moduleTemplateUrl: (module: string, resource: string, projectId?: number | string | null) =>
    apiPath(`module-tools/${encodeURIComponent(module)}/${encodeURIComponent(resource)}/template`)
      + (projectId === null || projectId === undefined ? '' : `?projectId=${encodeURIComponent(String(projectId))}`),
  previewModuleImport: (
    module: string,
    resource: string,
    payload: FormData,
    projectId?: number | string | null,
  ) =>
    api.upload<{
      fileName: string;
      total: number;
      creates: number;
      updates: number;
      valid: number;
      invalid: number;
      rows: Array<{
        row: number;
        recordId?: number;
        action: 'create' | 'update';
        values: Record<string, unknown>;
        errors: string[];
      }>;
    }>(
      `module-tools/${module}/${resource}/import/preview`
        + (projectId === null || projectId === undefined ? '' : `?projectId=${encodeURIComponent(String(projectId))}`),
      payload,
    ),
  confirmModuleImport: (
    module: string,
    resource: string,
    rows: Array<Record<string, unknown>>,
    projectId?: number | string | null,
  ) => api.post<{
    total: number;
    created: number;
    updated: number;
    items: ModuleRecord[];
  }>(`module-tools/${module}/${resource}/import/confirm`, {
    rows,
    ...(projectId === null || projectId === undefined ? {} : { projectId: Number(projectId) }),
  }),
  /** 一个项目下某资源的附件计数（新附件域），问题清单「附件」列用 */
  moduleAttachmentCounts: (module: string, resource: string, projectId: string | number) =>
    api.get<Record<string, number>>(
      `module-tools/${module}/${resource}/attachment-counts?projectId=${encodeURIComponent(String(projectId))}`),
  moduleAttachments: (module: string, resource: string, recordId: string | number) =>
    api.get<{
      attachments: Array<{
        id: number;
        fileName: string;
        mimeType: string;
        sha256: string;
        sizeBytes: number;
        note?: string;
        uploadedBy?: string;
        createdAt: string;
        /** 我能不能撤下它——后端算好的，前端不另判一套 */
        canRemove?: boolean;
        /** 撤不了的原因，直接显示给人看 */
        removeBlockedReason?: string | null;
      }>;
    }>(`module-tools/${module}/${resource}/${recordId}/attachments`),
  /**
   * 撤下一个附件（0920 Tony：「他们上传的资料不对也删除不掉了」）。
   * 后端是**打标记不是真删**：文件留着可追溯，界面上不再出现。
   */
  /**
   * 被驳回的变更，申请人改完重新送审（0920 Tony:「应该直接退回给申请人」）。
   * 第 3 步的审批级全部清空，从第 1 级重走；上一轮的结论进审计。
   */
  resubmitChange: (changeId: string | number) =>
    api.post<{ resubmitted: true; round: number }>(
      `module-tools/changes/${changeId}/resubmit`, {},
    ),
  removeModuleAttachment: (
    module: string,
    resource: string,
    recordId: string | number,
    attachmentId: string | number,
    reason?: string,
  ) => api.delete<Record<string, unknown>>(
    `module-tools/${module}/${resource}/${recordId}/attachments/${attachmentId}`,
    reason ? { reason } : undefined,
  ),
  uploadModuleAttachment: (
    module: string,
    resource: string,
    recordId: string | number,
    payload: FormData,
  ) => api.upload<Record<string, unknown>>(
    `module-tools/${module}/${resource}/${recordId}/attachments`,
    payload,
  ),
  /**
   * 问题/8D 清单含图 ZIP（清单.csv + images/…）。
   * ids 为当前可见行的问题内部 ID；可选 projectId 限定项目页导出。
   */
  issuesExportWithAttachmentsUrl: (
    ids: Array<string | number>,
    projectId?: string | number,
  ) => {
    const q = new URLSearchParams();
    q.set('ids', ids.map(String).join(','));
    if (projectId !== undefined && projectId !== null && String(projectId) !== '') {
      q.set('projectId', String(projectId));
    }
    return apiPath(`module-tools/issues/issues/export-with-attachments?${q.toString()}`);
  },
  moduleAttachmentDownloadUrl: (
    module: string,
    resource: string,
    recordId: string | number,
    attachmentId: string | number,
  ) => apiPath(
    `module-tools/${encodeURIComponent(module)}/${encodeURIComponent(resource)}/${recordId}/attachments/${attachmentId}/download`,
  ),
  /**
   * 附件的「直接看」地址：同一个下载地址加 inline=1。
   *
   * 后端只对图片和 PDF 认这个参数，别的类型照旧当下载处理——
   * zip / xlsx inline 出来是一屏乱码，不如老实下载（module-tools.controller 里的 PREVIEWABLE）。
   */
  moduleAttachmentPreviewUrl: (
    module: string,
    resource: string,
    recordId: string | number,
    attachmentId: string | number,
  ) => {
    const base = eqsApi.moduleAttachmentDownloadUrl(module, resource, recordId, attachmentId);
    return `${base}${base.includes('?') ? '&' : '?'}inline=1`;
  },
  /* ---------- 问题清单的编辑权限（0823 Tony 拍板的分层） ---------- */
  /**
   * 这个项目下每条问题「我能改什么」，key 是问题 ID。
   * 前端只拿它灰掉格子；真正的拦截在下面两个写入接口的后端。
   */
  issueEditRights: (projectId: string | number) =>
    api.get<Record<string, IssueEditRights>>(
      `module-tools/issues/edit-rights?projectId=${encodeURIComponent(String(projectId))}`),
  /** 问题升级（0911）：这条问题在哪一级、各级由谁接、我能不能升降、履历 */
  issueEscalationTargets: (issueId: string | number) =>
    api.get<IssueEscalationTargets>(`module-tools/issues/${issueId}/escalation`),
  /** 升级 / 降级：写层级、记履历、通知这一级该接的人 */
  escalateIssue: (issueId: string | number, payload: { toLevel: number; reason: string }) =>
    api.post<ModuleRecord & { escalation?: { toLevel: number; notified: number; targets: string[] } }>(
      `module-tools/issues/${issueId}/escalate`, payload),
  /** 管理层视图：升到部门负责人 / 管理层的未关问题 + 按判据该升的 */
  issueEscalationBoard: () =>
    api.get<IssueEscalationBoard>('module-tools/issues/escalation-board'),
  /**
   * 登记里程碑挽回计划（0923）：逾期后写「挽回到哪天 + 怎么挽回」，项目灯从红落到黄。
   * 本项目项目经理、管理员、director 能填；挽回日期不能早于今天，措施必填。
   */
  registerMilestoneRecovery: (
    projectId: string | number,
    milestoneId: string | number,
    payload: { recoveryDate: string; note: string },
  ) => api.post<Milestone>(`portfolio/projects/${projectId}/milestones/${milestoneId}/recovery`, payload),
  /** 项目经理自评灯：只能往上调；写原因 */
  declareProjectStatus: (projectId: string | number, payload: { status: HealthStatus; reason: string }) =>
    api.post<ProjectDeclaration>(`portfolio/projects/${projectId}/declaration`, payload),
  /**
   * 改问题内容 / 实际完成时间。
   *
   * 走 module-tools 这条路而不是通用的 modules PATCH：通用路由只认「项目里能不能写」，
   * 认不出提出者和责任人，做不了「责任人不能改完成时间」这一层。
   */
  updateIssueRecord: (issueId: string | number, payload: Record<string, unknown>) =>
    api.patch<ModuleRecord>(`module-tools/issues/${issueId}`, payload),
  /** 改问题状态；关闭仍然只有质量能点，后端两道门叠着 */
  advanceIssueRecord: (issueId: string | number, status: string) =>
    api.post<ModuleRecord>(`module-tools/issues/${issueId}/status`, { status }),
  /**
   * 取一条问题的最新全文。升级看板写进展前先取一次：进展是追加的流水，
   * 看板只带最近几条，拿它拼接会把更早的记录冲掉。
   */
  issueRecord: (issueId: string | number) =>
    api.get<ModuleRecord>(`modules/issues/issues/${issueId}`),
  dashboard: () => api.get<Record<string, unknown>>('dashboard'),
  recalculateDashboard: () => api.post<Record<string, unknown>>('dashboard/recalculate', {}),
};
